import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
} from '@racefront/shadcn-ui'
import {
  CalendarCheck,
  CircleCheck,
  ExternalLink,
  LineChart,
  Loader2,
  TriangleAlert,
  Trophy,
  Users,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link, useSearchParams } from 'react-router-dom'
import { APP_ROUTES } from '../../../constants/routes'
import { TERMS_URL } from '../../constants'
import { useAuth } from '../../data/hooks/auth'
import { useGrantRole } from './data/use-grant-role'

const FOR_ORGANIZERS_URL = 'https://www.pacesports.app/en/for-organizers'

const BENEFITS = [
  {
    icon: CalendarCheck,
    id: 'Auth.BecomeOrganizer.BenefitEvents',
    defaultMessage: 'Create and publish your own events',
  },
  {
    icon: Users,
    id: 'Auth.BecomeOrganizer.BenefitParticipants',
    defaultMessage: 'Manage registrations and participants',
  },
  {
    icon: LineChart,
    id: 'Auth.BecomeOrganizer.BenefitInsights',
    defaultMessage: 'Follow sales and results in real time',
  },
] as const

export default function BecomeOrganizer() {
  const [searchParams] = useSearchParams()
  const redirectUri = searchParams.get('redirectUri')

  const [cooperationAgreement, setCooperationAgreement] = useState(false)
  const [dataProcessingAgreement, setDataProcessingAgreement] = useState(false)

  const { grantRole, isLoading, error, result } = useGrantRole()
  const { refreshToken } = useAuth()
  const [isFinishing, setIsFinishing] = useState(false)

  const isBusy = isLoading || isFinishing
  const canContinue = cooperationAgreement && dataProcessingAgreement && !isBusy

  // Where to send the user when they're done: back where they came from, or the
  // admin app when the redirect was lost (e.g. the page was opened directly).
  const returnTo = redirectUri || import.meta.env.VITE_ADMIN_URL || APP_ROUTES.Account

  const goToReturnTo = useCallback(() => {
    // replace() so the opt-in page doesn't sit in history behind the admin app.
    window.location.replace(returnTo)
  }, [returnTo])

  const handleContinue = useCallback(async () => {
    const outcome = await grantRole('organizer')

    // 'failed' surfaces the error alert; 'already-granted' switches to a screen
    // with its own CTA back. Neither should redirect on its own.
    if (outcome !== 'granted') return

    setIsFinishing(true)
    try {
      // The access token used for the grant predates the new Cognito group, so
      // `organizer` won't show up in claims until the tokens are refreshed.
      await refreshToken()
    } catch {
      // A failed refresh isn't fatal: the role is granted server-side, and the
      // admin app will refresh (or re-authenticate) on its own when it loads.
    }

    goToReturnTo()
  }, [grantRole, refreshToken, goToReturnTo])

  // The account already holds the role, so there is nothing to accept. Show a way
  // back rather than re-presenting the agreements or bouncing them automatically.
  if (result === 'already-granted') {
    return (
      <div className="mx-auto flex w-full max-w-lg flex-col gap-6 px-4 py-8">
        <div className="space-y-3 text-center">
          <div className="bg-primary/10 mx-auto w-fit rounded-full p-4">
            <CircleCheck className="text-primary h-8 w-8" />
          </div>
          <h1 className="text-3xl font-bold">
            <FormattedMessage id="Auth.BecomeOrganizer.AlreadyTitle" defaultMessage="You're already an organizer" />
          </h1>
          <p className="text-muted-foreground">
            <FormattedMessage
              id="Auth.BecomeOrganizer.AlreadyDescription"
              defaultMessage="Your account already has the organizer role, so there's nothing to accept here."
            />
          </p>
        </div>
        <Button className="w-full" size="lg" onClick={goToReturnTo}>
          {redirectUri ? (
            <FormattedMessage id="Auth.BecomeOrganizer.GoBack" defaultMessage="Go back" />
          ) : (
            <FormattedMessage id="Auth.BecomeOrganizer.GoToAdmin" defaultMessage="Go to organizer tools" />
          )}
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6 px-4 py-8">
      <div className="space-y-3 text-center">
        <div className="bg-primary/10 mx-auto w-fit rounded-full p-4">
          <Trophy className="text-primary h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold">
          <FormattedMessage id="Auth.BecomeOrganizer.Title" defaultMessage="Build your event in minutes!" />
        </h1>
        <p className="text-muted-foreground">
          <FormattedMessage
            id="Auth.BecomeOrganizer.Subtitle"
            defaultMessage="Accept our terms and conditions to enable your access to our organizer tools. You'll keep using the same account to manage your racer profile."
          />
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <FormattedMessage id="Auth.BecomeOrganizer.BenefitsTitle" defaultMessage="As an organizer you can" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {BENEFITS.map(({ icon: Icon, id, defaultMessage }) => (
            <div key={id} className="flex items-center gap-3">
              <Icon className="text-primary h-5 w-5 shrink-0" />
              <span className="text-sm">
                <FormattedMessage id={id} defaultMessage={defaultMessage} />
              </span>
            </div>
          ))}

          <a
            href={FOR_ORGANIZERS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary inline-flex items-center gap-1 pt-1 text-sm underline"
          >
            <FormattedMessage
              id="Auth.BecomeOrganizer.LearnMore"
              defaultMessage="Learn more about organizing with Pace"
            />
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <FormattedMessage id="Auth.BecomeOrganizer.TermsTitle" defaultMessage="Accept the organizer terms" />
          </CardTitle>
          <CardDescription>
            <FormattedMessage
              id="Auth.BecomeOrganizer.TermsDescription"
              defaultMessage="Organizing events on the platform requires these two agreements."
            />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-row items-start space-x-3 rounded-md border p-4">
            <Checkbox
              id="cooperationAgreement"
              checked={cooperationAgreement}
              onCheckedChange={(checked) => setCooperationAgreement(checked === true)}
              className="notranslate mt-0.5"
              translate="no"
            />
            <div className="space-y-1 leading-none">
              <label htmlFor="cooperationAgreement" className="cursor-pointer text-sm font-medium">
                <FormattedMessage
                  id="Auth.SignUp.AcceptCooperation"
                  defaultMessage="I accept Cooperation Agreement (T&C)"
                />
              </label>
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="Auth.SignUp.CooperationDescriptionHTML"
                  defaultMessage="You agree to our {link}"
                  values={{
                    link: (
                      <Link className="text-primary underline" to={TERMS_URL} target="_blank" rel="noopener noreferrer">
                        <FormattedMessage id="Auth.SignUp.CooperationLink" defaultMessage="Cooperation Agreement ↗" />
                      </Link>
                    ),
                  }}
                />
              </p>
            </div>
          </div>

          <div className="flex flex-row items-start space-x-3 rounded-md border p-4">
            <Checkbox
              id="dataProcessingAgreement"
              checked={dataProcessingAgreement}
              onCheckedChange={(checked) => setDataProcessingAgreement(checked === true)}
              className="notranslate mt-0.5"
              translate="no"
            />
            <div className="space-y-1 leading-none">
              <label htmlFor="dataProcessingAgreement" className="cursor-pointer text-sm font-medium">
                <FormattedMessage
                  id="Auth.SignUp.AcceptDataProcessing"
                  defaultMessage="Accept Data Processing Agreement"
                />
              </label>
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="Auth.SignUp.DataProcessingDescriptionHTML"
                  defaultMessage="You agree to our {link}"
                  values={{
                    link: (
                      <Link className="text-primary underline" to={TERMS_URL} target="_blank" rel="noopener noreferrer">
                        <FormattedMessage
                          id="Auth.SignUp.DataProcessingLink"
                          defaultMessage="Data Processing Agreement ↗"
                        />
                      </Link>
                    ),
                  }}
                />
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>
            <FormattedMessage
              id="Auth.BecomeOrganizer.ErrorTitle"
              defaultMessage="We couldn't add the organizer role"
            />
          </AlertTitle>
          <AlertDescription>
            {error.code === 401 ? (
              <FormattedMessage
                id="Auth.BecomeOrganizer.ErrorSession"
                defaultMessage="Your session has expired. Please sign in again and retry."
              />
            ) : error.code === 404 ? (
              <FormattedMessage
                id="Auth.BecomeOrganizer.ErrorAccount"
                defaultMessage="We couldn't find your account. Please sign in again, or contact support if this continues."
              />
            ) : (
              <FormattedMessage
                id="Auth.BecomeOrganizer.ErrorGeneric"
                defaultMessage="Something went wrong on our end and the role wasn't added. Please try again — if it keeps happening, contact support."
              />
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-3">
        <Button className="flex w-full gap-2" size="lg" disabled={!canContinue} onClick={handleContinue}>
          {isBusy && <Loader2 className="h-4 w-4 animate-spin" />}
          {error ? (
            <FormattedMessage id="Auth.BecomeOrganizer.Retry" defaultMessage="Try again" />
          ) : (
            <FormattedMessage id="Auth.BecomeOrganizer.Continue" defaultMessage="Become an organizer" />
          )}
        </Button>
        <Button variant="ghost" className="w-full" asChild>
          <a href={import.meta.env.VITE_WEB_URL || '/account'}>
            <FormattedMessage id="Auth.BecomeOrganizer.Decline" defaultMessage="No thanks, take me back" />
          </a>
        </Button>
      </div>
    </div>
  )
}
